/* eslint-disable react-hooks/set-state-in-effect */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useQueryClient } from '@tanstack/react-query';
import { useBatchMutation } from '@bmwe/data';
import {
  CreateRoleAssignmentDocument,
  DeleteRoleAssignmentDocument,
  UpdateRoleAssignmentDocument,
  useInfiniteGroupsQuery,
  useInfiniteUserPicklistQuery,
  useObjectRoleAssignmentsQuery,
} from '@bmwe/data/shared';
import type { RoleType, UserModel } from '../../types';
import { appManagementStrings, commonStrings } from '../../i18n';
import { roleTypeOptions } from '../../helper';
import { PickList } from '../../components';
import { PickListHeader } from './PickListHeader';
import { AssignmentItemDisplay } from './AssignmentItemDisplay';
import { useSaveRoleAssignments } from './useSaveRoleAssignments';
import { useToast } from '../../context';
import './AccessManager.css';
import { DeleteRoleAssignmentCommandInput } from '@bmwe/data/awg/graphql';
import {
  CreateRoleAssignmentCommandInput,
  UpdateRoleAssignmentCommandInput,
} from '@bmwe/data/kwg/graphql';

// Extend existing types for local state needs
interface AssignmentItem extends UserModel {
  roleType: RoleType;
  isGroup: boolean;
  groupId?: string;
}

export interface AccessManagerProps {
  visible: boolean;
  objectType: string;
  objectId?: string;
  onHide: () => void;
}

const ROLE_OPTIONS = roleTypeOptions
  .filter((option) => option.value !== 'none')
  .map((option) => ({
    label: option.name,
    value: option.value,
  }));

export function AccessManager({
  visible,
  objectType,
  objectId,
  onHide,
}: AccessManagerProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  const [groupSearch, setGroupSearch] = useState('');

  // Target lists (final state)
  const [targetUsers, setTargetUsers] = useState<AssignmentItem[]>([]);
  const [targetGroups, setTargetGroups] = useState<AssignmentItem[]>([]);

  // Fetch existing role assignments
  const {
    data: roleAssignmentsData,
    isLoading: isLoadingAssignments,
    isError: isErrorAssignments,
  } = useObjectRoleAssignmentsQuery(
    {
      where: {
        and: [
          { objectType: { eq: objectType } },
          ...(objectId
            ? [{ dossierId: { eq: objectId } }]
            : [{ dossierId: { eq: null } }]),
        ],
      },
    },
    {
      enabled: visible,
    },
  );

  // Invalidate cache when dialog opens to force fresh fetch
  useEffect(() => {
    if (visible) {
      void queryClient.invalidateQueries({
        queryKey: ['ObjectRoleAssignments'],
      });
    }
  }, [visible, queryClient]);

  // Reset UI state when dialog closes
  useEffect(() => {
    if (!visible) {
      setActiveTab(0);
      setUserSearch('');
      setGroupSearch('');
      setTargetUsers([]);
      setTargetGroups([]);
    }
  }, [visible]);

  // Initialize target lists from fetched data (runs on every data change)
  useEffect(() => {
    if (!visible || !roleAssignmentsData?.roleAssignments?.edges) {
      return;
    }

    const users: AssignmentItem[] = [];
    const groups: AssignmentItem[] = [];

    roleAssignmentsData.roleAssignments.edges.forEach((edge) => {
      const node = edge?.node;
      if (!node) {
        return;
      }

      if (node.user) {
        users.push({
          id: node.user.id,
          name: node.user.name || '',
          email: node.user.email || '',
          roleType: node.roleType as RoleType,
          isGroup: false,
        });
      }

      if (node.group) {
        groups.push({
          id: node.group.id,
          name: node.group.name || '',
          email: undefined,
          roleType: node.roleType as RoleType,
          isGroup: true,
          groupId: node.group.id,
        });
      }
    });

    setTargetUsers(users);
    setTargetGroups(groups);
  }, [visible, roleAssignmentsData]);

  // Fetch users with infinite scroll
  const {
    data: usersData,
    fetchNextPage: fetchNextUserPage,
    hasNextPage: hasNextUserPage,
    isLoading: isLoadingUsers,
  } = useInfiniteUserPicklistQuery(
    {
      order: { name: 'ASC' },
      first: 50,
      where: userSearch
        ? {
            or: [
              { name: { contains: userSearch } },
              { email: { contains: userSearch } },
            ],
          }
        : undefined,
    },
    {
      enabled: visible && activeTab === 0,
      initialPageParam: {},
      getNextPageParam: (lastPage) => {
        if (!lastPage?.users?.pageInfo?.hasNextPage) {
          return undefined;
        }
        return { after: lastPage.users.pageInfo.endCursor };
      },
    },
  );

  // Fetch groups with infinite scroll
  const {
    data: groupsData,
    fetchNextPage: fetchNextGroupPage,
    hasNextPage: hasNextGroupPage,
    isLoading: isLoadingGroups,
  } = useInfiniteGroupsQuery(
    {
      order: [{ name: 'ASC' }],
      first: 50,
      where: groupSearch ? { name: { contains: groupSearch } } : undefined,
    },
    {
      enabled: visible && activeTab === 1,
      initialPageParam: {},
      getNextPageParam: (lastPage) => {
        if (!lastPage?.groups?.pageInfo?.hasNextPage) {
          return undefined;
        }
        return { after: lastPage.groups.pageInfo.endCursor };
      },
    },
  );

  const batchMutation = useBatchMutation();

  const { save } = useSaveRoleAssignments();

  // Compute source lists (all fetched - target)
  const sourceUsers = useMemo<AssignmentItem[]>(() => {
    if (!usersData?.pages) {
      return [];
    }

    const allUsers: AssignmentItem[] = [];
    usersData.pages.forEach((page) => {
      page.users?.edges?.forEach((edge) => {
        if (!edge.node) {
          return;
        }
        allUsers.push({
          id: edge.node.id,
          name: edge.node.name || '',
          email: edge.node.email || '',
          roleType: 'READER',
          isGroup: false,
        });
      });
    });

    // Filter out users already in target
    const targetUserIds = new Set(targetUsers.map((u) => u.id));
    return allUsers.filter((u) => !targetUserIds.has(u.id));
  }, [usersData, targetUsers]);

  const sourceGroups = useMemo<AssignmentItem[]>(() => {
    if (!groupsData?.pages) {
      return [];
    }

    const allGroups: AssignmentItem[] = [];
    groupsData.pages.forEach((page) => {
      page.groups?.edges?.forEach((edge) => {
        if (!edge.node) {
          return;
        }
        allGroups.push({
          id: edge.node.id,
          name: edge.node.name || '',
          roleType: 'READER',
          isGroup: true,
          groupId: edge.node.id,
        });
      });
    });

    // Filter out groups already in target
    const targetGroupIds = new Set(targetGroups.map((g) => g.groupId));
    return allGroups.filter((g) => !targetGroupIds.has(g.groupId));
  }, [groupsData, targetGroups]);

  // Handle picklist changes
  const handleUserChange = useCallback(
    (event: { source: AssignmentItem[]; target: AssignmentItem[] }) => {
      setTargetUsers(event.target);
    },
    [],
  );

  const handleGroupChange = useCallback(
    (event: { source: AssignmentItem[]; target: AssignmentItem[] }) => {
      setTargetGroups(event.target);
    },
    [],
  );

  // Handle role changes in target list
  const handleUserRoleChange = useCallback(
    (userId: string | undefined, newRole: RoleType) => {
      if (!userId) {
        return;
      }
      setTargetUsers((prev) =>
        prev.map((u) => {
          if (u.id === userId) {
            return { ...u, roleType: newRole };
          }
          return u;
        }),
      );
    },
    [],
  );

  const handleGroupRoleChange = useCallback(
    (groupId: string | undefined, newRole: RoleType) => {
      if (!groupId) {
        return;
      }
      setTargetGroups((prev) =>
        prev.map((g) => {
          if (g.groupId === groupId) {
            return { ...g, roleType: newRole };
          }
          return g;
        }),
      );
    },
    [],
  );

  const handleSave = useCallback(async () => {
    if (!roleAssignmentsData?.roleAssignments?.edges) {
      return;
    }

    const originalAssignments = roleAssignmentsData.roleAssignments.edges
      .map((e) => e?.node)
      .filter(Boolean);

    const operations = save(
      originalAssignments,
      targetUsers,
      targetGroups,
      objectType,
      objectId,
    );

    const hasAnyChanges =
      operations.create.length > 0 ||
      operations.update.length > 0 ||
      operations.delete.length > 0;

    if (!hasAnyChanges) {
      onHide();
      return;
    }

    try {
      const mutations: Array<{ query: string; variables?: unknown }> = [];

      operations.delete.forEach((deleteOp) => {
        mutations.push({
          query: DeleteRoleAssignmentDocument,
          variables: {
            input: deleteOp satisfies DeleteRoleAssignmentCommandInput,
          },
        });
      });

      operations.create.forEach((createOp) => {
        mutations.push({
          query: CreateRoleAssignmentDocument,
          variables: {
            input: {
              ...createOp,
              roleType: createOp.roleType as RoleType,
            } satisfies CreateRoleAssignmentCommandInput,
          },
        });
      });

      operations.update.forEach((updateOp) => {
        mutations.push({
          query: UpdateRoleAssignmentDocument,
          variables: {
            input: {
              ...updateOp,
              roleType: updateOp.roleType as RoleType,
            } satisfies UpdateRoleAssignmentCommandInput,
          },
        });
      });

      await batchMutation.mutateAsync(mutations);

      await queryClient.invalidateQueries({
        queryKey: ['ObjectRoleAssignments'],
      });

      toast.showSuccess(
        commonStrings.words.success,
        appManagementStrings.accessManager.saveSuccess,
      );

      onHide();
    } catch (error) {
      console.error('Error saving role assignments:', error);

      let message = appManagementStrings.accessManager.errorSaving;
      if (error && typeof error === 'object') {
        if ('message' in error && typeof error.message === 'string') {
          message = error.message;
        }
      }

      toast.showError(commonStrings.words.error, message);
    }
  }, [
    roleAssignmentsData,
    targetUsers,
    targetGroups,
    objectType,
    objectId,
    save,
    batchMutation,
    queryClient,
    toast,
    onHide,
  ]);

  // Item templates
  const userItemTemplate = useCallback(
    (item: AssignmentItem, isTarget: boolean) => {
      return (
        <AssignmentItemDisplay
          name={item.name}
          email={item.email ?? undefined}
          isTarget={isTarget}
          roleType={item.roleType}
          roleOptions={ROLE_OPTIONS}
          onRoleChange={(newRole) => {
            handleUserRoleChange(item.id, newRole);
          }}
          rolePlaceholder={appManagementStrings.roleAssignment.selectRole}
        />
      );
    },
    [handleUserRoleChange],
  );

  const groupItemTemplate = useCallback(
    (item: AssignmentItem, isTarget: boolean) => {
      return (
        <AssignmentItemDisplay
          name={item.name}
          isTarget={isTarget}
          roleType={item.roleType}
          roleOptions={ROLE_OPTIONS}
          onRoleChange={(newRole) => {
            handleGroupRoleChange(item.groupId, newRole);
          }}
          rolePlaceholder={appManagementStrings.roleAssignment.selectRole}
        />
      );
    },
    [handleGroupRoleChange],
  );

  // Source headers with search and load more
  const userSourceHeader = (
    <PickListHeader
      search={userSearch}
      onSearchChange={setUserSearch}
      searchPlaceholder={appManagementStrings.accessManager.searchUsers}
      hasNextPage={hasNextUserPage}
      onLoadMore={fetchNextUserPage}
      isLoading={isLoadingUsers}
      loadMoreLabel={appManagementStrings.accessManager.loadMore}
    />
  );

  const groupSourceHeader = (
    <PickListHeader
      search={groupSearch}
      onSearchChange={setGroupSearch}
      searchPlaceholder={appManagementStrings.accessManager.searchGroups}
      hasNextPage={hasNextGroupPage}
      onLoadMore={fetchNextGroupPage}
      isLoading={isLoadingGroups}
      loadMoreLabel={appManagementStrings.accessManager.loadMore}
    />
  );

  const footer = (
    <div className="access-manager-footer">
      <Button label={commonStrings.words.cancel} outlined onClick={onHide} />
      <Button
        label={appManagementStrings.accessManager.saveChanges}
        onClick={handleSave}
        loading={batchMutation.isPending}
      />
    </div>
  );

  if (isLoadingAssignments) {
    return (
      <Dialog
        header={appManagementStrings.accessManager.title}
        visible={visible}
        onHide={onHide}
        className="access-manager-dialog"
        footer={footer}
      >
        <div className="access-manager-loading">
          {appManagementStrings.accessManager.loading}
        </div>
      </Dialog>
    );
  }

  if (isErrorAssignments) {
    return (
      <Dialog
        header={appManagementStrings.accessManager.title}
        visible={visible}
        onHide={onHide}
        className="access-manager-dialog"
        footer={footer}
      >
        <div className="access-manager-error">
          {appManagementStrings.accessManager.errorLoading}
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      header={appManagementStrings.accessManager.title}
      visible={visible}
      onHide={onHide}
      className="access-manager-dialog"
      footer={footer}
    >
      <div className="access-manager-tabs">
        <div className="access-manager-tab-headers">
          <button
            className={`access-manager-tab-header ${activeTab === 0 ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(0);
            }}
          >
            {appManagementStrings.accessManager.userAssignments}
          </button>
          <button
            className={`access-manager-tab-header ${activeTab === 1 ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(1);
            }}
          >
            {appManagementStrings.accessManager.groupAssignments}
          </button>
        </div>

        <div className="access-manager-tab-content">
          {activeTab === 0 && (
            <PickList
              source={sourceUsers}
              target={targetUsers}
              onChange={handleUserChange}
              itemTemplate={userItemTemplate}
              sourceHeader={userSourceHeader}
              targetHeader={appManagementStrings.accessManager.assignedUsers}
            />
          )}
          {activeTab === 1 && (
            <PickList
              source={sourceGroups}
              target={targetGroups}
              onChange={handleGroupChange}
              itemTemplate={groupItemTemplate}
              sourceHeader={groupSourceHeader}
              targetHeader={appManagementStrings.accessManager.assignedGroups}
            />
          )}
        </div>
      </div>
    </Dialog>
  );
}
